/**
 * „Jetzt bitte nicht hinauswerfen" — Gegenstueck zum Leerlauf-Rueckzug.
 *
 * Nach 30 Minuten ohne Eingabe schliesst App.tsx das Projekt und gibt die
 * Sperre frei (`shouldExitForIdle` in projectLockLogic). Das ist fast immer
 * richtig — aber nicht, solange Eingaben allein im Speicher einer Komponente
 * liegen und mit ihr verschwinden wuerden. Der mobile Erfassungs-Assistent ist
 * genau dieser Fall: mobileWizardLock haelt seine Maske sogar dann stehen,
 * wenn die Sperre kippt, damit der Pruefer seine Eingaben noch abschreiben
 * kann. Ein automatischer Rueckzug wuerde diese Zusage brechen.
 *
 * Warum ein Modul-Register und keine Prop-Kette: der Assistent haengt drei
 * Ebenen unter App.tsx (App → SiGeKoInspectionModule → MobileInspectionWizard),
 * und die naechste schutzwuerdige Maske haengt woanders. Eine Flagge durch
 * jede Zwischenebene zu reichen waere bei jeder neuen Stelle wieder Arbeit —
 * und genau die vergisst man.
 *
 * Absichtlich ohne React und ohne DOM: eine Anmeldung ist ein Aufruf, die
 * Abmeldung die zurueckgegebene Funktion. In einer Komponente ist das der
 * Rueckgabewert eines Effekts und damit auch bei einem harten Abbau sicher.
 */

interface IdleHold {
    reason: string;
}

/**
 * Ein Eintrag je Anmeldung, NICHT je Grund: zwei Masken mit demselben Grund
 * duerfen sich nicht gegenseitig abmelden. Der zurueckgegebene Abmelder kennt
 * seinen eigenen Eintrag und raeumt nur diesen weg.
 */
const holds = new Set<IdleHold>();

/**
 * Meldet einen Grund an, der den Leerlauf-Rueckzug verhindert.
 *
 * @returns Die Abmeldung. Mehrfaches Aufrufen ist harmlos.
 */
export const holdIdleExit = (reason: string): (() => void) => {
    const entry: IdleHold = { reason };
    holds.add(entry);
    return () => {
        holds.delete(entry);
    };
};

/** Haelt gerade irgendetwas den Rueckzug auf? */
export const isIdleExitHeld = (): boolean => holds.size > 0;

/** Die angemeldeten Gruende — fuer Protokollausgaben und Tests. */
export const idleExitHoldReasons = (): string[] => Array.from(holds, (h) => h.reason);

/**
 * Gruende der Upload-Halte: ein Eintrag aus dem Assistenten, eine Freimeldung
 * oder ein Kartenfoto wird noch hochgeladen und steht noch nicht im Bericht.
 * Solange einer steht, sperren Senden und „Entwurf verwerfen“ — der Versand
 * schriebe sonst den Stand ohne ihn zurueck.
 */
export const UPLOAD_HALTE = {
    eintrag: 'eintrag-wird-gespeichert',
    freimeldung: 'freimeldung-wird-gespeichert',
    foto: 'foto-wird-hochgeladen',
} as const;

/**
 * Laufende Uploads — getrennt vom Halt oben: der Leerlauf-Halt ist begrenzt
 * (ein haengender Upload soll Rueckzug und Neuladen nicht ewig sperren), die
 * Sperre fuer Senden und Verwerfen gilt dagegen bis zum echten Ende. Sonst
 * ginge nach Ablauf der Frist ein Bericht ohne das ausstehende Ergebnis hinaus.
 */
let laufendeUploads = 0;

/** Meldet einen laufenden Upload an; die Rueckgabe meldet genau ihn ab (mehrfach harmlos). */
export const uploadBeginnt = (): (() => void) => {
    laufendeUploads += 1;
    let offen = true;
    return () => {
        if (!offen) return;
        offen = false;
        laufendeUploads -= 1;
    };
};

/** Laeuft gerade ein Upload, dessen Ergebnis noch nicht im Bericht steht? */
export const uploadLaeuft = (): boolean => laufendeUploads > 0;

/** Alles abmelden. Nur fuer Tests und den Abbau einer Sitzung gedacht. */
export const clearIdleExitHolds = (): void => {
    holds.clear();
};
